/**
 * EmailCaptureGlanceCard
 * ----------------------
 * Compact hero tile showing the four numbers MODs care about most:
 *   - Capture Rate (with goal context)
 *   - Captured (with "of X to hit goal" subtext)
 *   - Transactions logged so far today
 *   - "X more to hit goal" CTA (or 🔥 once at-goal)
 *
 * Pulls every value from a single `tallyData` prop (same shape served
 * by `/api/tally/{date}`) so it can never drift out of sync with the
 * deeper stats grid + GoalCalculator on the Manager Dashboard or
 * the live preview on the Zone Sheet.
 *
 * Used by:
 *   - ManagerDashboard.js — top of main area, above the 4-card stats grid.
 *   - ZoneSheetPage.js → EyesOnBizSection — above the AdjustEmailsPopover
 *     + CheckpointEntry, so floor MODs see the email number while
 *     they're entering checkpoint readings. (User ask 2026-06-15
 *     Phase 2 — "managers need this visible while running the floor".)
 */
import { Envelope, Check, Warning } from "@phosphor-icons/react";

const EmailCaptureGlanceCard = ({ tallyData, settings, hideSectionLabel = false }) => {
  if (!tallyData) return null;
  const storeTx = tallyData.store_transactions || 0;
  const totalEmails = tallyData.total_emails || 0;
  const captureRate = tallyData.capture_rate || 0;
  const goalRate = tallyData.goal_rate || 0.35;
  const emailsForGoal = tallyData.emails_for_goal || 0;
  const neededGoal = tallyData.emails_needed_goal || 0;
  const atGoal = !!tallyData.at_goal;

  const ratePct = (captureRate * 100).toFixed(1);
  const goalPct = (goalRate * 100).toFixed(0);

  return (
    <section
      className="relative mb-3 rounded-3xl border border-slate-200/70 bg-white p-4 sm:p-5 shadow-md ring-1 ring-slate-100 overflow-hidden"
      data-testid="email-capture-glance"
      aria-label={`${settings?.app_name || "Wow App"} — Email Capture at a Glance`}
    >
      {/* Accent ribbon — FB blue → teal */}
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #0066FF 0%, #6366f1 50%, #14b8a6 100%)" }} />
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #0066FF, #6366f1)" }}>
            <Envelope weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            {!hideSectionLabel && (
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-blue-600 leading-none">
                Eyes on the Biz
              </p>
            )}
            <p className={`text-lg font-black text-slate-900 leading-tight ${hideSectionLabel ? "" : "mt-1"}`} style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Email Capture · At a Glance
            </p>
          </div>
        </div>
        {storeTx > 0 ? (
          atGoal ? (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white shadow-sm"
              style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }}
              data-testid="glance-status"
            >
              <Check weight="bold" className="w-3 h-3" /> At Goal
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white shadow-sm"
              style={{ background: "linear-gradient(135deg, #f43f5e, #f97316)" }}
              data-testid="glance-status"
            >
              <Warning weight="bold" className="w-3 h-3" /> Below Goal
            </span>
          )
        ) : (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 ring-1 ring-slate-200"
            data-testid="glance-status"
          >
            Awaiting first read
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="rounded-2xl px-3.5 py-3 bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100" data-testid="glance-rate-block">
          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-black mb-0.5">Capture Rate</p>
          <p
            className={`text-3xl sm:text-4xl font-black leading-none ${atGoal ? "text-emerald-600" : "text-slate-900"}`}
            data-testid="glance-capture-rate"
          >
            {ratePct}%
          </p>
          <p className="text-[10px] text-slate-600 mt-1 font-bold">Goal {goalPct}%</p>
        </div>

        <div className="rounded-2xl px-3.5 py-3 bg-gradient-to-br from-violet-50 to-fuchsia-50 ring-1 ring-violet-100" data-testid="glance-emails-block">
          <p className="text-[10px] uppercase tracking-wider text-violet-700 font-black mb-0.5">Captured</p>
          <p className="text-3xl sm:text-4xl font-black leading-none text-slate-900" data-testid="glance-emails">
            {totalEmails}
          </p>
          <p className="text-[10px] text-slate-600 mt-1 font-bold">of {emailsForGoal} to hit goal</p>
        </div>

        <div className="rounded-2xl px-3.5 py-3 bg-gradient-to-br from-teal-50 to-cyan-50 ring-1 ring-teal-100" data-testid="glance-tx-block">
          <p className="text-[10px] uppercase tracking-wider text-teal-700 font-black mb-0.5">Transactions</p>
          <p className="text-3xl sm:text-4xl font-black leading-none text-slate-900" data-testid="glance-tx">
            {storeTx}
          </p>
          <p className="text-[10px] text-slate-600 mt-1 font-bold">so far today</p>
        </div>

        <div className={`rounded-2xl px-3.5 py-3 ring-1 ${atGoal ? "bg-gradient-to-br from-emerald-50 to-teal-50 ring-emerald-200" : storeTx > 0 ? "bg-gradient-to-br from-pink-50 to-rose-50 ring-pink-200" : "bg-slate-50 ring-slate-200"}`} data-testid="glance-cta-block">
          <p className={`text-[10px] uppercase tracking-wider font-black mb-0.5 ${atGoal ? "text-emerald-700" : storeTx > 0 ? "text-pink-700" : "text-slate-500"}`}>
            {atGoal ? "Stay Above" : "To Hit Goal"}
          </p>
          {atGoal ? (
            <>
              <p className="text-3xl sm:text-4xl font-black leading-none text-emerald-600" data-testid="glance-cta-number">
                🔥
              </p>
              <p className="text-[10px] text-emerald-700 mt-1 font-black">Keep stacking emails</p>
            </>
          ) : storeTx > 0 ? (
            <>
              <p className="text-3xl sm:text-4xl font-black leading-none text-pink-600" data-testid="glance-cta-number">
                +{neededGoal}
              </p>
              <p className="text-[10px] text-pink-700 mt-1 font-black">
                more email{neededGoal === 1 ? "" : "s"} needed
              </p>
            </>
          ) : (
            <>
              <p className="text-3xl sm:text-4xl font-black leading-none text-slate-300" data-testid="glance-cta-number">
                —
              </p>
              <p className="text-[10px] text-slate-500 mt-1 font-bold">Log a checkpoint to see</p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default EmailCaptureGlanceCard;
